import { Role } from '@/Enums/Users/Role.enum';
import type { IPaginatedApiResponse } from '@/Interfaces/ApiResponse/PaginatedApiResponse.interface';
import type { IClientControlOption, IWhitelabelFilterOption } from '@/Interfaces/ControlOption.interface';
import { IFilterFormData } from '@/Interfaces/PdfBuilder/FilterFormData.interface';
import { IScheduledPdfCustomReport } from '@/Interfaces/PdfBuilder/ScheduledPdfCustomReport.interface';
import type { ICustomReport } from '@/Interfaces/ReportBuilder/CustomReport.interface';
import { IScheduleFormData } from '@/Interfaces/ReportBuilder/ScheduleFormData.interface';
import { SCHEDULED_PDF_CUSTOM_REPORT_ENDPOINT } from '@/constants/endpoints';
import { getPdfBuilderPayload } from '@/helpers/pdfBuilder';
import type { PaginationState, PayloadSortState } from '@/types/commonFilters.types';
import axios from 'axios';
import { toast } from 'react-toastify';

const PDF_CUSTOM_REPORT_ENDPOINT = '/pdf-custom-report';

export const generatePdf = async (
  formData: IFilterFormData,
  role: Role,
  clients: IClientControlOption[],
  whitelabels: IWhitelabelFilterOption[],
) => {
  await axios.post(PDF_CUSTOM_REPORT_ENDPOINT, getPdfBuilderPayload(formData, role, clients, whitelabels));
};

export const fetchPdfReports = async (
  params: PaginationState & Partial<PayloadSortState>,
  signal: AbortSignal | undefined,
): Promise<IPaginatedApiResponse<ICustomReport[]>> => {
  const response = await axios.get(PDF_CUSTOM_REPORT_ENDPOINT, { signal, params });

  return response.data;
};

export const downloadPdfReport = async (reportId: number | undefined) => {
  if (!reportId) return;
  try {
    const response = await axios.get(`${PDF_CUSTOM_REPORT_ENDPOINT}/${reportId}/download`);

    const downloadUrl = response.data;
    if (!downloadUrl) {
      toast.error('Download failed. The report is either empty or failed to generate.');
      return;
    }
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = '';
    a.click();
    a.remove();
  } catch (error) {
    toast.error('Failed to download report. Please try again.');
    console.error('Download failed:', error);
  }
};

/**
 + * Builds scheduled report parameters from filter form data and user role.
 + * Extracts advertiser IDs, platforms, and report selections while setting filterDate
 + * to the form's dateRangeFormat for scheduled delivery.
 + */
const buildScheduledPdfReportParams = (
  filterFormData: IFilterFormData,
  role: Role,
  clients: IClientControlOption[],
  whitelabels: IWhitelabelFilterOption[],
) => {
  const {
    filters: { advertiserIds },
    platforms,
    deliveryReports,
    attributionReports,
  } = getPdfBuilderPayload(filterFormData, role, clients, whitelabels);

  return {
    filterDate: filterFormData.dateRangeFormat,
    advertiserIds,
    platforms,
    deliveryReports,
    attributionReports,
  };
};

export const schedulePdfReport = async (
  scheduleFormData: IScheduleFormData,
  filterFormData: IFilterFormData,
  role: Role,
  clients: IClientControlOption[],
  whitelabels: IWhitelabelFilterOption[],
) => {
  const { reportName, startDate, recipientEmails, interval } = scheduleFormData;
  return await axios.post(SCHEDULED_PDF_CUSTOM_REPORT_ENDPOINT, {
    reportParams: buildScheduledPdfReportParams(filterFormData, role, clients, whitelabels),
    name: reportName,
    deliveryStartAt: startDate.toISOString(true),
    recipientEmails,
    interval,
  });
};

export const updateScheduledPdfReport = async (
  id: number,
  scheduleFormData: Pick<
    IScheduleFormData,
    'reportName' | 'interval' | 'startDate' | 'recipientEmails' | 'dateRangeFormat'
  >,
) => {
  const { reportName, interval, startDate, recipientEmails, dateRangeFormat } = scheduleFormData;
  return await axios.patch(`${SCHEDULED_PDF_CUSTOM_REPORT_ENDPOINT}/${id}`, {
    name: reportName,
    interval,
    recipientEmails,
    deliveryStartAt: startDate.toISOString(true),
    reportParams: { filterDate: dateRangeFormat },
  });
};

export const fetchScheduledPdfReports = async (
  params: PaginationState & Partial<PayloadSortState>,
  signal: AbortSignal | undefined,
): Promise<IPaginatedApiResponse<IScheduledPdfCustomReport[]>> => {
  const response = await axios.get(SCHEDULED_PDF_CUSTOM_REPORT_ENDPOINT, { signal, params });

  return response.data;
};

export const cancelScheduledPdfReport = async (id: number | undefined) => {
  if (!id) {
    throw new Error('No id to cancel the scheduled pdf report by');
  }
  return await axios.delete(`${SCHEDULED_PDF_CUSTOM_REPORT_ENDPOINT}/${id}`);
};
